import type {
  GenerationSourceEntry,
  GenerationSourcePick,
} from '../../../deck/materials';
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownLabel,
  DropdownTrigger,
} from '../../components/Dropdown';
import type {
  GenerateSourceDropdownItemsProps,
  GenerateSourceMenuProps,
} from './DeckMaterials.types';
import { generationSourceIcon } from './icons';

/**
 * "Generate from…" — the deck's materials as menu rows.
 *
 * The rows are the SAME data on both platforms (`generationSourceEntries`)
 * and the SAME primitive underneath (`DropdownItem` is a `MenuListItem`);
 * only the shell differs, which is the one thing that genuinely does. Web and
 * tablet get the dropdown below; the phone renders the same entries in a
 * bottom sheet, because a phone's surface has no anchor room for a popover.
 *
 * A row that cannot be generated from is DISABLED WITH A REASON rather than
 * hidden — a document sitting in the grid above and missing from this menu
 * reads as a bug, where "still being processed" reads as an answer.
 */
export function GenerateSourceDropdownItems({
  entries,
  onPick,
  trailing,
  testID,
}: GenerateSourceDropdownItemsProps) {
  return (
    <>
      {entries.map((entry) => (
        <DropdownItem
          key={entry.key}
          icon={generationSourceIcon(entry.kind)}
          disabled={entry.disabled}
          description={entry.hint}
          rightSlot={trailing?.(entry) ?? undefined}
          onSelect={() => onPick(entry.pick)}
          testID={testID ? `${testID}-${entry.key}` : undefined}>
          {entry.label}
        </DropdownItem>
      ))}
    </>
  );
}

/** The full dropdown, trigger injected by the host — the empty state's big
 *  CTA and the canvas's "Regenerate" chip are the same menu behind two
 *  buttons. */
export function DeckMaterialsGenerateMenu({
  entries,
  onPick,
  renderTrigger,
  triggerFullWidth,
  triggerClassName,
  label,
  matchTriggerWidth,
  trailing,
  side = 'bottom',
  align = 'center',
  size = 's',
  testID,
}: GenerateSourceMenuProps) {
  return (
    <Dropdown size={size} testID={testID}>
      <DropdownTrigger fullWidth={triggerFullWidth} className={triggerClassName}>
        {renderTrigger}
      </DropdownTrigger>
      <DropdownContent
        side={side}
        align={align}
        minWidth={200}
        matchTriggerWidth={matchTriggerWidth}>
        {label ? <DropdownLabel>{label}</DropdownLabel> : null}
        <GenerateSourceDropdownItems
          entries={entries}
          onPick={onPick}
          trailing={trailing}
          testID={testID}
        />
      </DropdownContent>
    </Dropdown>
  );
}
(DeckMaterialsGenerateMenu as { displayName?: string }).displayName =
  'DeckMaterialsGenerateMenu';

export type { GenerationSourceEntry, GenerationSourcePick };
